import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { newestFirst } from "../lib/format";
import { LANGS, useI18n } from "../lib/i18n";
import { COUNTRIES, COUNTRY_META, getCountry, type Country } from "../lib/country";
import type {
  ChannelConfig,
  ChannelLanguage,
  ChannelProvider,
  NotificationChannel,
  NotificationChannelInput,
} from "../lib/types";
import { Button, Card, ErrorBox, Field, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { ChannelSetupGuide } from "../components/channel-setup-guide";

const PROVIDER_COLOR: Record<ChannelProvider, "info" | "primary"> = {
  telegram: "info",
  lark: "primary",
};

/** Các trường config theo từng provider — quyết định ô nào hiện trong form. */
const PROVIDER_FIELDS: Record<ChannelProvider, string[]> = {
  telegram: ["botToken", "chatId", "messageThreadId"],
  lark: ["domain", "appId", "appSecret", "chatId"],
};

const SECRET_FIELDS = new Set(["botToken", "appSecret"]);

/** Nhãn ngôn ngữ tin nhắn bot — viết bằng chính ngôn ngữ đó (dùng chung với LangSwitch). */
const LANGUAGE_LABEL: Record<ChannelLanguage, string> = LANGS.reduce(
  (acc, l) => ({ ...acc, [l.code]: l.label }),
  {} as Record<ChannelLanguage, string>,
);

/** Kênh Lark đang ở chế độ broadcast (gửi tới mọi nhóm bot đang ở)? */
export function isBroadcast(config: ChannelConfig | undefined): boolean {
  const v = config?.broadcast;
  return v === true || v === "true";
}

/** Chuỗi mô tả nhóm đích (chat_id) của kênh để hiển thị trong bảng. */
function targetOf(ch: NotificationChannel, allGroups: string): string {
  if (ch.provider === "lark" && isBroadcast(ch.config)) return allGroups;
  const chatId = ch.config.chatId;
  return typeof chatId === "string" && chatId ? chatId : "—";
}

export function NotificationChannels() {
  const { t, fmtAgo, fmtDate } = useI18n();
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const { data, loading, error, reload } = useAsync(
    () => api.notificationChannels.list(countryFilter),
    [countryFilter],
  );
  const [editing, setEditing] = useState<NotificationChannel | "new" | null>(null);
  const [cloning, setCloning] = useState<NotificationChannel | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // ok: true=thành công, false=lỗi, null=đang gửi (hiện severity info)
  const [flash, setFlash] = useState<{ ok: boolean | null; msg: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const channels = data ?? [];

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function test(ch: NotificationChannel) {
    // Broadcast: tin thử sẽ tới MỌI nhóm bot đang ở ⇒ hỏi lại trước khi gửi.
    if (
      ch.provider === "lark" &&
      isBroadcast(ch.config) &&
      !confirm(t("chan.lk.testBroadcastConfirm", { name: ch.name }))
    )
      return;
    setBusy(`test-${ch.id}`);
    setActionError(null);
    // Phản hồi tức thì trong lúc gọi mạng tới Telegram/Lark (có thể vài giây).
    setFlash({ ok: null, msg: t("chan.testing", { name: ch.name }) });
    try {
      const res = await api.notificationChannels.test(ch.id);
      setFlash(
        res.ok
          ? { ok: true, msg: t("chan.testOk", { name: ch.name }) }
          : { ok: false, msg: t("chan.testFail", { error: res.error ?? "?" }) },
      );
      await reload();
    } catch (e) {
      setFlash({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  async function testError(ch: NotificationChannel) {
    setBusy(`test-error-${ch.id}`);
    setActionError(null);
    setFlash({ ok: null, msg: t("chan.testingError", { name: ch.name }) });
    try {
      const res = await api.notificationChannels.testError(ch.id);
      setFlash(
        res.ok
          ? { ok: true, msg: t("chan.testErrorOk", { name: ch.name }) }
          : { ok: false, msg: t("chan.testFail", { error: res.error ?? "?" }) },
      );
      await reload();
    } catch (e) {
      setFlash({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  async function remove(ch: NotificationChannel) {
    if (!confirm(t("chan.confirmDelete", { name: ch.name }))) return;
    await run(`del-${ch.id}`, () => api.notificationChannels.remove(ch.id));
  }

  const columns = useMemo<GridColDef<NotificationChannel>[]>(
    () => [
      {
        field: "name",
        headerName: t("chan.name"),
        flex: 1.2,
        minWidth: 170,
        renderCell: ({ row }) => (
          <Stack sx={{ lineHeight: 1.3, py: 0.5 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Chip
              size="small"
              color={PROVIDER_COLOR[row.provider]}
              label={t(`chan.provider.${row.provider}`)}
              sx={{ width: "fit-content", mt: 0.25 }}
            />
          </Stack>
        ),
      },
      {
        field: "target",
        headerName: t("chan.col.target"),
        flex: 1,
        minWidth: 150,
        valueGetter: (_v, row) => targetOf(row, t("chan.lk.allGroups")),
        renderCell: ({ row }) =>
          // Broadcast: nhãn "mọi nhóm" (không phải chat_id) ⇒ không dùng font mono.
          row.provider === "lark" && isBroadcast(row.config) ? (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label={t("chan.lk.allGroups")}
              sx={{ width: "fit-content" }}
            />
          ) : (
            <Typography variant="body2" noWrap className="mono">
              {targetOf(row, t("chan.lk.allGroups"))}
            </Typography>
          ),
      },
      {
        field: "brand",
        headerName: t("chan.brand"),
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) =>
          row.brand ? (
            <Chip size="small" variant="outlined" label={row.brand} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t("chan.brandAll")}
            </Typography>
          ),
      },
      {
        field: "country",
        headerName: t("chan.country"),
        flex: 0.7,
        minWidth: 120,
        renderCell: ({ row }) => {
          const meta = COUNTRY_META[row.country as Country];
          return (
            <Chip
              size="small"
              variant="outlined"
              label={meta ? `${meta.flag} ${meta.label}` : row.country}
            />
          );
        },
      },
      {
        // Ngôn ngữ bot dùng khi gửi vào nhóm này (khác ngôn ngữ CMS đang xem).
        field: "language",
        headerName: t("chan.col.language"),
        flex: 0.6,
        minWidth: 110,
        valueGetter: (_v, row) => LANGUAGE_LABEL[row.language] ?? row.language,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            label={LANGUAGE_LABEL[row.language] ?? row.language}
          />
        ),
      },
      {
        field: "throttleMinutes",
        headerName: t("chan.col.throttle"),
        flex: 0.6,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {row.throttleMinutes > 0
              ? t("chan.minutes", { n: row.throttleMinutes })
              : t("chan.noThrottle")}
          </Typography>
        ),
      },
      {
        // Công tắc bật/tắt bấm trực tiếp trên từng dòng — mỗi kênh độc lập.
        field: "enabled",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 130,
        renderCell: ({ row }) => (
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={row.enabled}
                disabled={busy === `toggle-${row.id}`}
                onChange={() =>
                  run(`toggle-${row.id}`, () =>
                    api.notificationChannels.update(row.id, {
                      enabled: !row.enabled,
                    }),
                  )
                }
              />
            }
            label={
              <Typography
                variant="caption"
                color={row.enabled ? "success.main" : "text.secondary"}
              >
                {row.enabled ? t("chan.status.on") : t("chan.status.off")}
              </Typography>
            }
          />
        ),
      },
      {
        field: "lastSentAt",
        headerName: t("chan.lastSent"),
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ row }) => (
          <Stack sx={{ lineHeight: 1.3, py: 0.5, minWidth: 0 }}>
            <Tooltip title={row.lastSentAt ? fmtDate(row.lastSentAt) : ""}>
              <Typography variant="body2" noWrap>
                {row.lastSentAt ? fmtAgo(row.lastSentAt) : t("chan.never")}
              </Typography>
            </Tooltip>
            {row.lastError && (
              <Tooltip title={row.lastError}>
                <Typography variant="caption" color="error" noWrap>
                  {row.lastError}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="test"
            icon={<SendIcon fontSize="small" />}
            label={t("chan.test")}
            disabled={busy === `test-${row.id}`}
            onClick={() => test(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="testError"
            icon={<SendIcon fontSize="small" color="warning" />}
            label={t("chan.testError")}
            disabled={busy === `test-error-${row.id}`}
            onClick={() => testError(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="clone"
            icon={<ContentCopyIcon fontSize="small" />}
            label={t("chan.clone")}
            onClick={() => setCloning(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon fontSize="small" />}
            label={t("common.edit")}
            onClick={() => setEditing(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon fontSize="small" />}
            label={t("common.delete")}
            onClick={() => remove(row)}
            showInMenu
          />,
        ],
      },
    ],
    [t, fmtAgo, fmtDate, busy],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("chan.title")}</h1>
          <p className="muted">
            {t("chan.subtitle", {
              on: channels.filter(c => c.enabled).length,
              total: channels.length,
            })}
          </p>
        </div>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            select
            size="small"
            label={t("chan.countryFilter")}
            value={countryFilter}
            onChange={e => setCountryFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">{t("chan.countryAll")}</MenuItem>
            {COUNTRIES.map(c => (
              <MenuItem key={c} value={c}>
                {COUNTRY_META[c].flag} {COUNTRY_META[c].label}
              </MenuItem>
            ))}
          </TextField>
          <Button onClick={() => setGuideOpen(true)}>
            <HelpOutlineIcon sx={{ fontSize: 18, mr: 0.5 }} />
            {t("chan.guide")}
          </Button>
          <Button variant="primary" onClick={() => setEditing("new")}>
            {t("chan.add")}
          </Button>
        </Stack>
      </header>

      {error && <ErrorBox message={error} />}
      {actionError && <ErrorBox message={actionError} />}
      {flash && (
        <Alert
          severity={flash.ok === null ? "info" : flash.ok ? "success" : "error"}
          variant="outlined"
          icon={
            flash.ok === null ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          onClose={flash.ok === null ? undefined : () => setFlash(null)}
        >
          {flash.msg}
        </Alert>
      )}

      <Card>
        {loading ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(channels, "createdAt")}
            columns={columns}
            showToolbar
            loading={busy !== null}
            getRowClassName={({ row }) => (row.enabled ? "" : "row-disabled")}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </Card>

      {editing && (
        <ChannelForm
          channel={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onGuide={() => setGuideOpen(true)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {cloning && (
        <CloneChannelModal
          channel={cloning}
          onClose={() => setCloning(null)}
          onDone={msg => {
            setCloning(null);
            setFlash({ ok: true, msg });
            reload();
          }}
        />
      )}

      {guideOpen && <ChannelSetupGuide onClose={() => setGuideOpen(false)} />}
    </div>
  );
}

/** Form tạo/sửa kênh — các ô config đổi theo provider đang chọn. */
function ChannelForm({
  channel,
  onClose,
  onSaved,
  onGuide,
}: {
  channel: NotificationChannel | null;
  onClose: () => void;
  onSaved: () => void;
  onGuide: () => void;
}) {
  const { t, lang } = useI18n();
  const [name, setName] = useState(channel?.name ?? "");
  const [provider, setProvider] = useState<ChannelProvider>(
    channel?.provider ?? "telegram",
  );
  const [country, setCountry] = useState<Country>(channel?.country ?? getCountry());
  const [brand, setBrand] = useState(channel?.brand ?? "");
  // Ngôn ngữ tin nhắn bot gửi vào nhóm — mặc định theo ngôn ngữ CMS đang dùng.
  const [language, setLanguage] = useState<ChannelLanguage>(
    channel?.language ?? (lang as ChannelLanguage),
  );
  const { data: brandsData } = useAsync(() => api.brands.list(), []);
  const brands = brandsData ?? [];
  const [notifyUnknown, setNotifyUnknown] = useState(channel?.notifyUnknown ?? true);
  const [enabled, setEnabled] = useState(channel?.enabled ?? true);
  const [throttle, setThrottle] = useState(String(channel?.throttleMinutes ?? 10));
  // Giữ mọi giá trị config đã gõ (kể cả khi đổi provider) để không mất dữ liệu.
  // Chỉ lấy các giá trị chuỗi — `broadcast` (boolean) có state riêng bên dưới.
  const [config, setConfig] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(channel?.config ?? {})) {
      if (typeof v === "string") out[k] = v;
    }
    return out;
  });
  // Lark: true = gửi tới mọi nhóm bot đang ở (khuyến nghị), false = 1 nhóm cố định.
  const [broadcast, setBroadcast] = useState(
    channel ? isBroadcast(channel.config) : true,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Danh sách nhóm Lark (backend lấy từ appId/appSecret). null = chưa tải.
  const [larkChats, setLarkChats] = useState<{ chatId: string; name: string }[] | null>(
    null,
  );
  const [chatsBusy, setChatsBusy] = useState(false);
  const [chatsError, setChatsError] = useState<string | null>(null);

  const setCfg =
    (k: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value } = e.target;
      // Đổi App ID / App Secret / máy chủ ⇒ danh sách nhóm cũ không còn đúng.
      if (k === "appId" || k === "appSecret" || k === "domain") {
        setLarkChats(null);
        setChatsError(null);
      }
      setConfig(c => ({ ...c, [k]: value }));
    };

  // App Secret sẵn sàng: đã gõ mới, hoặc đang sửa kênh (secret đã lưu ở backend).
  const appSecretReady =
    (config.appSecret ?? "").trim() !== "" || !!channel?.config?.appSecret;
  // Đủ điều kiện để chọn nhóm: có App ID + App Secret.
  const canPickChat = (config.appId ?? "").trim() !== "" && appSecretReady;

  async function fetchLarkChats() {
    if (!canPickChat || chatsBusy) return;
    setChatsBusy(true);
    setChatsError(null);
    try {
      const secret = (config.appSecret ?? "").trim();
      const res = await api.notificationChannels.larkChats({
        appId: (config.appId ?? "").trim(),
        // Secret đã che (••…) / bỏ trống ⇒ backend dùng secret đã lưu của kênh.
        appSecret: secret && !secret.startsWith("•") ? secret : undefined,
        domain: config.domain ?? "lark",
        id: channel?.id,
      });
      setLarkChats(res.chats);
    } catch (e) {
      setLarkChats([]);
      setChatsError(e instanceof Error ? e.message : String(e));
    } finally {
      setChatsBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    // Chỉ gửi các trường config thuộc provider đang chọn.
    const cfg: ChannelConfig = {};
    for (const key of PROVIDER_FIELDS[provider]) {
      const v = (config[key] ?? "").trim();
      // Secret bỏ trống ⇒ không gửi (giữ giá trị cũ ở backend).
      if (SECRET_FIELDS.has(key) && v === "") continue;
      // Broadcast: backend tự liệt kê nhóm ⇒ không gửi chatId.
      if (key === "chatId" && provider === "lark" && broadcast) continue;
      cfg[key] = v;
    }
    if (provider === "lark") cfg.broadcast = broadcast;
    const payload: NotificationChannelInput = {
      name: name.trim(),
      provider,
      enabled,
      country,
      language,
      brand: brand.trim() === "" ? null : brand.trim(),
      config: cfg,
      notifyUnknown,
      throttleMinutes: Math.max(0, Number(throttle) || 0),
    };
    try {
      if (channel) await api.notificationChannels.update(channel.id, payload);
      else await api.notificationChannels.create(payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const secretHint = channel ? t("chan.secretKeep") : undefined;

  return (
    <Modal
      title={channel ? t("chan.formEdit", { id: channel.id }) : t("chan.formNew")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}

        {/* Lối tắt tới hướng dẫn lấy token/webhook cho provider tương ứng. */}
        <Alert
          severity="info"
          variant="outlined"
          icon={<HelpOutlineIcon fontSize="small" />}
          action={
            <Button type="button" onClick={onGuide}>
              {t("chan.guide")}
            </Button>
          }
          sx={{ alignItems: "center", py: 0.25 }}
        >
          {t("chan.guideHint")}
        </Alert>

        <Field label={t("chan.name")} hint={t("chan.nameHint")}>
          <input value={name} onChange={e => setName(e.target.value)} required />
        </Field>

        <div className="form-row">
          <Field label={t("chan.provider")} hint={t("chan.providerHint")}>
            <select
              value={provider}
              onChange={e => setProvider(e.target.value as ChannelProvider)}
            >
              <option value="telegram">{t("chan.provider.telegram")}</option>
              <option value="lark">{t("chan.provider.lark")}</option>
            </select>
          </Field>
          <Field label={t("chan.country")} hint={t("chan.countryHint")}>
            <select
              value={country}
              onChange={e => setCountry(e.target.value as Country)}
            >
              {COUNTRIES.map(c => (
                <option key={c} value={c}>
                  {COUNTRY_META[c].flag} {COUNTRY_META[c].label} ({c.toUpperCase()})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="form-row">
          <Field label={t("chan.brand")} hint={t("chan.brandHint")}>
            <select value={brand} onChange={e => setBrand(e.target.value)}>
              <option value="">{t("chan.brandAll")}</option>
              {brands.map(b => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("chan.language")} hint={t("chan.languageHint")}>
            <select
              value={language}
              onChange={e => setLanguage(e.target.value as ChannelLanguage)}
            >
              {LANGS.map(l => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* ==== Trường theo provider ==== */}
        {provider === "telegram" && (
          <>
            <Alert
              severity="info"
              variant="outlined"
              sx={{ mb: 1.5, py: 0.5, fontSize: "0.825rem" }}
            >
              {t("chan.tg.sharedBotTip")}
            </Alert>
            <Field label={t("chan.tg.botToken")} hint={secretHint ?? t("chan.tg.botTokenHint")}>
              <input
                value={config.botToken ?? ""}
                onChange={setCfg("botToken")}
                placeholder="123456:ABC-DEF…"
                required={!channel}
              />
            </Field>
            <div className="form-row">
              <Field label={t("chan.tg.chatId")} hint={t("chan.tg.chatIdHint")}>
                <input value={config.chatId ?? ""} onChange={setCfg("chatId")} required />
              </Field>
              <Field label={t("chan.tg.threadId")} hint={t("chan.tg.threadIdHint")}>
                <input
                  value={config.messageThreadId ?? ""}
                  onChange={setCfg("messageThreadId")}
                  inputMode="numeric"
                />
              </Field>
            </div>
          </>
        )}

        {provider === "lark" && (
          <>
              <Field label={t("chan.lk.domain")} hint={t("chan.lk.domainHint")}>
                <select value={config.domain ?? "lark"} onChange={setCfg("domain")}>
                  <option value="lark">Lark (larksuite.com)</option>
                  <option value="feishu">Feishu (feishu.cn)</option>
                </select>
              </Field>
              <Field label={t("chan.lk.appId")} hint={t("chan.lk.appIdHint")}>
                <input
                  value={config.appId ?? ""}
                  onChange={setCfg("appId")}
                  placeholder="cli_…"
                  required={!channel}
                />
              </Field>
            <Field label={t("chan.lk.appSecret")} hint={secretHint}>
              <input
                value={config.appSecret ?? ""}
                onChange={setCfg("appSecret")}
                required={!channel}
              />
            </Field>
            {/* Chọn nhóm đích: broadcast (mọi nhóm bot đang ở) hoặc 1 nhóm cố định. */}
            <Field
              label={t("chan.lk.mode")}
              hint={broadcast ? t("chan.lk.modeBroadcastHint") : t("chan.lk.modeChatHint")}
            >
              <select
                value={broadcast ? "broadcast" : "chat"}
                onChange={e => setBroadcast(e.target.value === "broadcast")}
              >
                <option value="broadcast">{t("chan.lk.modeBroadcast")}</option>
                <option value="chat">{t("chan.lk.modeChat")}</option>
              </select>
            </Field>

            {broadcast ? (
              /* Không cần chat_id — chỉ cho xem trước bot đang ở những nhóm nào. */
              <Field
                label={t("chan.lk.preview")}
                hint={canPickChat ? t("chan.lk.previewHint") : t("chan.lk.chatIdLocked")}
              >
                <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Button
                    type="button"
                    onClick={fetchLarkChats}
                    disabled={!canPickChat}
                    loading={chatsBusy}
                  >
                    {t("chan.lk.previewBtn")}
                  </Button>
                  {larkChats && larkChats.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t("chan.lk.previewCount", { n: larkChats.length })}
                    </Typography>
                  )}
                </Stack>
                {larkChats && larkChats.length > 0 && (
                  <Stack
                    direction="row"
                    sx={{ gap: 0.5, flexWrap: "wrap", mt: 0.75 }}
                  >
                    {larkChats.map(c => (
                      <Chip key={c.chatId} size="small" variant="outlined" label={c.name} />
                    ))}
                  </Stack>
                )}
                {chatsError && (
                  <span className="field-hint" style={{ color: "var(--bad)" }}>
                    {chatsError}
                  </span>
                )}
                {larkChats && larkChats.length === 0 && !chatsError && (
                  <span className="field-hint">{t("chan.lk.noChats")}</span>
                )}
              </Field>
            ) : (
              /* Chat ID: Cho phép gõ/dán trực tiếp mã oc_... (khi không có quyền im:chat:readonly)
                 HOẶC tải danh sách nhóm để chọn nhanh theo tên. */
              <Field
                label={t("chan.lk.chatId")}
                hint={canPickChat ? t("chan.lk.chatIdHint") : t("chan.lk.chatIdLocked")}
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <input
                      value={config.chatId ?? ""}
                      onChange={setCfg("chatId")}
                      placeholder="oc_xxxxxxxxxxxxxxxxxxxx"
                      disabled={!canPickChat}
                      required
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="button"
                      onClick={fetchLarkChats}
                      disabled={!canPickChat}
                      loading={chatsBusy}
                    >
                      {t("chan.lk.previewBtn")}
                    </Button>
                  </Stack>
                  {larkChats && larkChats.length > 0 && (
                    <select
                      value={config.chatId ?? ""}
                      onChange={setCfg("chatId")}
                      disabled={!canPickChat || chatsBusy}
                    >
                      <option value="">{t("chan.lk.chooseGroup")}</option>
                      {larkChats.map(c => (
                        <option key={c.chatId} value={c.chatId}>
                          {c.name} — {c.chatId}
                        </option>
                      ))}
                    </select>
                  )}
                </Stack>
                {chatsError && (
                  <span className="field-hint" style={{ color: "var(--bad)" }}>
                    {chatsError}
                  </span>
                )}
                {larkChats && larkChats.length === 0 && !chatsError && (
                  <span className="field-hint">{t("chan.lk.noChats")}</span>
                )}
              </Field>
            )}
          </>
        )}

        <div className="form-row">
          <Field label={t("chan.col.throttle")} hint={t("chan.throttleHint")}>
            <input
              type="number"
              min={0}
              value={throttle}
              onChange={e => setThrottle(e.target.value)}
            />
          </Field>
        </div>

        <FormControlLabel
          control={
            <Switch
              checked={notifyUnknown}
              onChange={e => setNotifyUnknown(e.target.checked)}
            />
          }
          label={
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("chan.notifyUnknown")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("chan.notifyUnknownHint")}
              </Typography>
            </Stack>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />

        <FormControlLabel
          control={
            <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("chan.enabled")}
            </Typography>
          }
          sx={{ m: 0 }}
        />

        <div className="form-actions">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {channel ? t("chan.save") : t("chan.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Modal sao chép kênh sang quốc gia khác (giữ nguyên botToken/appSecret trong DB). */
function CloneChannelModal({
  channel,
  onClose,
  onDone,
}: {
  channel: NotificationChannel;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const { t } = useI18n();
  // Quốc gia đích mặc định: nước còn lại
  const defaultTarget: Country = channel.country === "vn" ? "br" : "vn";
  const [targetCountry, setTargetCountry] = useState<Country>(defaultTarget);
  const [name, setName] = useState<string>(
    () => `${channel.name} (${COUNTRY_META[defaultTarget]?.label ?? defaultTarget.toUpperCase()})`,
  );
  const [chatId, setChatId] = useState<string>("");
  const [messageThreadId, setMessageThreadId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const cfgOverrides: Record<string, any> = {};
      if (chatId.trim()) cfgOverrides.chatId = chatId.trim();
      if (channel.provider === "telegram" && messageThreadId.trim()) {
        cfgOverrides.messageThreadId = Number(messageThreadId.trim());
      }
      await api.notificationChannels.clone(channel.id, {
        targetCountry,
        name: name.trim(),
        config: Object.keys(cfgOverrides).length > 0 ? cfgOverrides : undefined,
      });
      const targetLabel = COUNTRY_META[targetCountry]?.label ?? targetCountry;
      onDone(t("chan.cloneSuccess", { country: targetLabel }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const srcMeta = COUNTRY_META[channel.country as Country];

  return (
    <Modal title={t("chan.cloneTitle")} onClose={busy ? () => {} : onClose}>
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}

        <Alert severity="info" variant="outlined" sx={{ py: 0.5, fontSize: "0.825rem" }}>
          {t("chan.cloneSubtitle")}
        </Alert>

        <Stack
          direction="row"
          sx={{
            p: 1.25,
            bgcolor: "action.hover",
            borderRadius: 1,
            gap: 1,
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("chan.cloneSource")}:
          </Typography>
          <Typography variant="body2">{channel.name}</Typography>
          <Chip
            size="small"
            variant="outlined"
            label={srcMeta ? `${srcMeta.flag} ${srcMeta.label}` : channel.country}
          />
        </Stack>

        <div className="form-row">
          <Field label={t("chan.cloneTargetCountry")}>
            <select
              value={targetCountry}
              onChange={e => {
                const tc = e.target.value as Country;
                setTargetCountry(tc);
                setName(`${channel.name} (${COUNTRY_META[tc]?.label ?? tc.toUpperCase()})`);
              }}
            >
              {COUNTRIES.map(c => (
                <option key={c} value={c}>
                  {COUNTRY_META[c].flag} {COUNTRY_META[c].label} ({c.toUpperCase()})
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("chan.cloneName")}>
            <input value={name} onChange={e => setName(e.target.value)} required />
          </Field>
        </div>

        {channel.provider === "telegram" ? (
          <div className="form-row">
            <Field label={t("chan.cloneChatId")} hint={t("chan.cloneChatIdHint")}>
              <input
                value={chatId}
                onChange={e => setChatId(e.target.value)}
                placeholder={channel.config.chatId ? `e.g. ${channel.config.chatId}` : "-100..."}
              />
            </Field>
            <Field label={t("chan.cloneThreadId")} hint={t("chan.cloneThreadIdHint")}>
              <input
                value={messageThreadId}
                onChange={e => setMessageThreadId(e.target.value)}
                placeholder="12345"
                inputMode="numeric"
              />
            </Field>
          </div>
        ) : (
          <Field label={t("chan.cloneChatId")} hint={t("chan.cloneChatIdHint")}>
            <input
              value={chatId}
              onChange={e => setChatId(e.target.value)}
              placeholder="oc_xxxxxxxxxxxxxxxxxxxx"
            />
          </Field>
        )}

        <Typography variant="caption" color="text.secondary">
          💡 {t("chan.cloneTip")}
        </Typography>

        <div className="form-actions" style={{ marginTop: 16 }}>
          <Button type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t("chan.cloneBtn")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

